import { z } from "zod";
import { uiRuntimeReleases } from "@secretaa/contracts-platform-data";
import { Uuid } from "@secretaa/http-contract";
// Surface pin IDs của Blueprint manifest đã compile; tmp_ chỉ thuộc proposal Build.
import { TimeZone } from "../work-os/schedule.ts";
import { Effect, Identity, Key, Label, Pointer, Ref } from "../work-os/primitives.ts";

export const SurfaceInputMapping = z.array(z.discriminatedUnion("source", [
  z.object({ source: z.enum(["FORM_FIELD", "RECORD_FIELD"]), sourceId: Key, fieldId: Uuid, targetPath: Pointer }).strict(),
  z.object({ source: z.literal("CURRENT_RECORD_ID"), targetPath: Pointer }).strict(),
])).max(100).superRefine((bindings, ctx) => {
  for (let i = 0; i < bindings.length; i++) for (let j = i + 1; j < bindings.length; j++) {
    const a = bindings[i]!.targetPath, b = bindings[j]!.targetPath;
    if (a === b || a.startsWith(b + "/") || b.startsWith(a + "/"))
      ctx.addIssue({ code: "custom", path: [j, "targetPath"], message: "OVERLAPPING_BINDINGS" });
  }
});

export const SurfaceType = uiRuntimeReleases.NativeRendererType;
export const DataSourceBinding = z.object({ id: Key, kind: z.enum(["PROJECT_QUERY", "READ_MODEL", "ARTIFACT"]),
  sourceRef: Ref, contractRef: Ref, fieldIds: z.array(Uuid).max(100), maxRows: z.number().int().min(1).max(200),
}).strict();
export const ActionBinding = z.object({ id: Key, label: Label, kind: z.enum(["COMMAND", "CAPABILITY", "FLOW"]), ref: Ref,
  inputContractRef: Ref, inputMapping: SurfaceInputMapping, targetConstraintRef: Ref, effect: Effect, confirmationPolicyRef: Ref,
}).strict();
const fields = z.array(Uuid).min(1).max(100);
const base = { ...Identity, title: Label, rendererContractVersion: z.number().int().positive(), dataSources: z.array(DataSourceBinding).min(1).max(16),
  actions: z.array(ActionBinding).max(32), policyRef: Ref, blueprintManifestRef: Ref.nullable(),
  refreshPolicy: z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("MANUAL") }).strict(),
    z.object({ mode: z.literal("POLL"), intervalSeconds: z.number().int().min(15).max(86400) }).strict(),
    z.object({ mode: z.literal("SUBSCRIBE"), sourceProfileRef: Ref }).strict(),
  ]),
};
const Column = z.object({ sourceId: Key, fieldIds: fields }).strict();
export const Widget = z.discriminatedUnion("type", [
  z.object({ id: Key, type: z.literal("METRIC"), sourceId: Key, metricContractRef: Ref }).strict(),
  z.object({ id: Key, type: z.enum(["TABLE", "STATUS_LIST"]), sourceId: Key, fieldIds: fields }).strict(),
  z.object({ id: Key, type: z.enum(["LINE_CHART", "BAR_CHART", "PIE_CHART"]), sourceId: Key, metricContractRef: Ref, categoryFieldId: Uuid, valueFieldId: Uuid }).strict(),
]);
export const AppSurfaceDefinitionVersion = z.discriminatedUnion("type", [
  z.object({ ...base, type: z.literal("TABLE"), columns: Column }).strict(),
  z.object({ ...base, type: z.literal("FORM"), form: Column, submitActionId: Key }).strict(),
  z.object({ ...base, type: z.literal("RECORD"), sections: z.array(Column).min(1).max(16) }).strict(),
  z.object({ ...base, type: z.literal("KANBAN"), cards: Column, groupByFieldId: Uuid, moveActionId: Key.nullable() }).strict(),
  z.object({ ...base, type: z.literal("INBOX"), list: Column, thread: Column, contextPanel: Column.nullable(),
    sendActionId: Key.nullable(), internalNoteActionId: Key.nullable(), assignmentActionId: Key.nullable(), statusActionId: Key.nullable(),
  }).strict(),
  z.object({ ...base, type: z.literal("DASHBOARD"), widgets: z.array(Widget).min(1).max(24) }).strict(),
  z.object({ ...base, type: z.literal("TIMELINE"), events: Column, timestampFieldId: Uuid }).strict(),
  z.object({ ...base, type: z.literal("CALENDAR"), events: Column, startFieldId: Uuid, endFieldId: Uuid.nullable(), timeZone: TimeZone, allDay: z.boolean() }).strict(),
]);
export const LocalNavigationDefinition = z.object({ ...Identity, entries: z.array(z.object({ id: Key, label: Label, surfaceRef: Ref, parentId: Key.nullable() }).strict()).max(64) }).strict();
export const SurfacePublishIntent = z.object({ workspaceId: Uuid, bindingId: Uuid, expectedBaseVersion: z.number().int().nonnegative(), draftRef: Ref,
  compiledDigest: z.string().regex(/^[a-f0-9]{64}$/), confirmationRef: Ref, idempotencyKey: Uuid,
}).strict();
export type AppSurfaceDefinitionVersion = z.infer<typeof AppSurfaceDefinitionVersion>;
export type LocalNavigationDefinition = z.infer<typeof LocalNavigationDefinition>;
